import {useMemo} from 'react'
import {type AppBskyFeedDefs} from '@atproto/api'

import {useSearchPostsQuery} from '#/state/queries/search-posts'
import {type Trend} from './trends'

/**
 * Returns the top posts for a trend, used to populate the inline post slots
 * on `HeroTrendCard` and `FeaturedTrendCard`.
 *
 * v1 strategy: hit Bluesky's `app.bsky.feed.searchPosts` directly. We query by
 * **`trend.title`** rather than the structured `trend.query` (keywords /
 * hashtags / languages). The richer query is meant to be expanded server-side
 * by W Trends (or mapped onto searchPosts' `tag`/`lang`/`since` params later);
 * until that lands, the trend title is the most reliable single term to hand to
 * Bluesky's search.
 *
 * Once the backend endpoint lands, swap the query inside this hook — every
 * consumer keeps working.
 *
 * Each consuming card fires its own search query. React Query dedupes by
 * `(query, sort)` key, so duplicate cards for the same trend share data.
 */
export function useTrendPosts(
  trend: Trend,
  {
    limit = 2,
    sort = 'top',
    enabled = true,
  }: {limit?: number; sort?: 'top' | 'latest'; enabled?: boolean} = {},
) {
  const {data, isFetching, error, isFetched} = useSearchPostsQuery({
    query: trend.title,
    sort,
    enabled,
  })

  const posts: AppBskyFeedDefs.PostView[] = useMemo(() => {
    if (!data) return []
    const seen = new Set<string>()
    const out: AppBskyFeedDefs.PostView[] = []
    for (const page of data.pages) {
      for (const post of page.posts) {
        if (seen.has(post.uri)) continue
        seen.add(post.uri)
        out.push(post)
        if (out.length >= limit) break
      }
      if (out.length >= limit) break
    }
    return out
  }, [data, limit])

  return {
    posts,
    isLoading: isFetching && posts.length === 0,
    isFetched,
    error,
  }
}
